import common from './common'
import {
  GOOGLE_INCLUDED_LANGUAGES,
  appLocaleToGoogleLang,
  googleLangToAppLocale,
} from './googleTranslateLocale'

export {
  APP_TO_GOOGLE_LANG,
  GOOGLE_INCLUDED_LANGUAGES,
  GOOGLE_TO_APP_LOCALE,
  appLocaleToGoogleLang,
  googleLangToAppLocale,
} from './googleTranslateLocale'

export function getGoogleTranslateSelect(targetElement) {
  if (!targetElement) return null
  return document.querySelector(`#${targetElement} select.goog-te-combo`)
    || document.querySelector(`#${targetElement} select`)
}

/**
 * Programmatically set goog-te-combo from an app locale (e.g. navbar / localStorage).
 */
export function applyGoogleTranslateLocale(appLocale, targetElement) {
  const selectElement = getGoogleTranslateSelect(targetElement)
  if (!selectElement) return false
  const googleLang = appLocaleToGoogleLang(appLocale)
  if (selectElement.value === googleLang) return true
  selectElement.value = googleLang
  selectElement.dispatchEvent(new Event('change'))
  return true
}

/**
 * Keep goog-te-combo ↔ localStorage.locale (and optional i18n callback) in sync.
 */
export function bindGoogleTranslateLocaleSync(targetElement, options = {}) {
  const { onLocaleChange = null } = options
  let applyingFromApp = false
  let selectEl = null
  let pollTimer = null
  let tries = 0

  const onComboChange = () => {
    if (applyingFromApp || !selectEl) return
    const googleLang = selectEl.value || 'en'
    const appLocale = googleLangToAppLocale(googleLang)
    const current = localStorage.getItem('locale') || 'en'
    if (current !== appLocale) {
      localStorage.setItem('locale', appLocale)
    }
    if (typeof onLocaleChange === 'function') {
      onLocaleChange(appLocale)
    }
  }

  const attach = () => {
    selectEl = getGoogleTranslateSelect(targetElement)
    if (!selectEl) return false
    selectEl.removeEventListener('change', onComboChange)
    selectEl.addEventListener('change', onComboChange)
    return true
  }

  const tryAttach = () => {
    if (attach()) {
      if (pollTimer) {
        clearInterval(pollTimer)
        pollTimer = null
      }
      return
    }
    tries += 1
    if (tries > 40 && pollTimer) {
      clearInterval(pollTimer)
      pollTimer = null
    }
  }

  tryAttach()
  pollTimer = setInterval(tryAttach, 250)

  return {
    unsubscribe() {
      if (pollTimer) {
        clearInterval(pollTimer)
        pollTimer = null
      }
      if (selectEl) {
        selectEl.removeEventListener('change', onComboChange)
        selectEl = null
      }
    },
    applyFromAppLocale(appLocale) {
      applyingFromApp = true
      try {
        applyGoogleTranslateLocale(appLocale, targetElement)
      } finally {
        setTimeout(() => {
          applyingFromApp = false
        }, 50)
      }
    },
  }
}

export default function setUpGoogleTranslate(targetElement) {
  function resetGoogleTranslate() {
    document.querySelectorAll('.goog-te-banner-frame, .goog-te-gadget').forEach(el => el.remove())

    const existingScript = document.querySelector('script[src*="translate.google.com"]')
    if (existingScript) {
      existingScript.remove()
    }

    const translateContainer = document.getElementById(targetElement)
    if (translateContainer) {
      translateContainer.innerHTML = ''
    }
  }

  window.googleTranslateElementInit = () => {
    if (window.isTranslationScriptCodeLoaded) return
    window.isTranslationScriptCodeLoaded = true

    const locale = localStorage.getItem('locale') || 'en'

    const translateContainer = document.getElementById(targetElement)
    if (!translateContainer) {
      console.error(`Google Translate target element "${targetElement}" not found.`)
      return
    }

    // eslint-disable-next-line no-new, no-undef
    new google.translate.TranslateElement(
      {
        pageLanguage: 'en',
        includedLanguages: GOOGLE_INCLUDED_LANGUAGES,
      },
      targetElement,
    )
    setTimeout(() => {
      applyGoogleTranslateLocale(locale, targetElement)
      const latest = localStorage.getItem('locale') || locale
      if (appLocaleToGoogleLang(latest) !== appLocaleToGoogleLang(locale)) {
        applyGoogleTranslateLocale(latest, targetElement)
      }
    }, 1600)
  }

  resetGoogleTranslate()

  if (!window.IS_LANG_TRANSLATE_SCRIPT_LOADED) {
    common.loadExternalCdn('//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit')
    window.IS_LANG_TRANSLATE_SCRIPT_LOADED = true
  } else {
    window.isTranslationScriptCodeLoaded = false
    window.googleTranslateElementInit()
  }

  const styleId = 'google-translate-style'
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style')
    style.id = styleId
    style.textContent = `
      .skiptranslate:not(.goog-te-gadget) { display: none !important; }
      body { top: 0 !important }
      .skiptranslate.goog-te-gadget { visibility: hidden !important; }
      .skiptranslate.goog-te-gadget div { visibility: visible; }
      select.goog-te-combo { padding: 4px; }
      .VIpgJd-ZVi9od-aZ2wEe-wOHMyf { display: none; }
    `
    document.head.appendChild(style)
  }
}
